// Barry Plant detail-page parser. Every Barry Plant property page is a
// Next.js page whose data embeds the listing record:
//   <script id="__NEXT_DATA__" type="application/json">
//   props.pageProps.propertyData
// Read that record, never pageProps.suburbData (suburb medians such as
// "median_sale":712500 and the suburb centroid) or similarProperties.
//
// Fields that look like a price but are not the sale price: price / priceView
// / price_value hold the campaign guide ("$600,000 - $650,000"). The sale
// price is soldDetails_price, shown only when soldDetails_price_display.
//
// soldDetails_date is "2026-05-11T00:00:00+10:00": local midnight of the sale
// day. The page renders it as "Sun 10th May 2026" because the site formats it
// in UTC; the date as written (2026-05-11) is the sale day, and it matches
// the record's own updated/created timestamps on that day.

import {
  areaValue,
  count,
  formatAddress,
  htmlText,
  isRecord,
  parseListingDate,
  propertyTypeLabel,
  stateCode,
  suburbName,
  text,
} from "../shared/listing-html.ts";

const NEXT_DATA_RE = /<script[^>]*\bid=["']__NEXT_DATA__["'][^>]*>([\s\S]*?)<\/script>/i;
const URL_ID_RE = /-(\d+)\/?(?:[?#].*)?$/;

function readPropertyData(html: string, url: string): Record<string, unknown> | null {
  const match = html.match(NEXT_DATA_RE);
  if (!match) return null;
  let data: unknown;
  try {
    data = JSON.parse(match[1]);
  } catch {
    return null;
  }
  const property = (data as { props?: { pageProps?: { propertyData?: unknown } } })?.props
    ?.pageProps?.propertyData;
  if (!isRecord(property)) return null;
  // The page must be the listing we asked for.
  const urlId = url.match(URL_ID_RE)?.[1];
  if (urlId && String(property.id) !== urlId) return null;
  return property;
}

function sqm(value: unknown, unit: unknown): string | null {
  const area = areaValue(value);
  if (!area) return null;
  if (unit === "squareMeter") return area;
  if (unit === "hectare") return String(Number(area) * 10_000);
  return null;
}

export function parseBarryPlantDetailHtml(html: string, url: string): Record<string, unknown> {
  const property = readPropertyData(html, url);
  if (!property) return {};

  const unit = text(property.address_subNumber);
  const streetNumber = text(property.address_streetNumber);
  const street = text(property.address_street);
  const suburb = suburbName(text(property.address_suburb));
  const state = stateCode(text(property.address_state));
  const postcode = text(property.address_postcode);
  const streetLine =
    streetNumber && street ? `${unit ? `${unit}/` : ""}${streetNumber} ${street}` : null;
  if (!streetLine) return {};

  const sold = text(property.status)?.toLowerCase() === "sold";
  const hit: Record<string, unknown> = {
    address: formatAddress(streetLine, suburb, state, postcode),
    suburb,
    state,
    postcode,
    listingType: sold
      ? "sold"
      : text(property.listing_type)?.toLowerCase() === "lease"
        ? "rent"
        : "buy",
  };

  hit.saleDate = sold ? parseListingDate(text(property.soldDetails_date)) : null;
  const recorded = property.soldDetails_price;
  const soldPrice = sold && typeof recorded === "number" && recorded >= 10_000 ? recorded : null;
  // An undisplayed sold price is withheld on the page: null, kept only for audit.
  const disclosed = property.soldDetails_price_display === true;
  hit.salePrice = disclosed ? soldPrice : null;
  hit.price = hit.salePrice;
  if (!disclosed && soldPrice !== null) hit.hiddenSalePrice = soldPrice;
  // The visible sold-price line, for auditing against the record.
  const display = html.match(/<span class="sold-price[^"]*">([^<]*)<\/span>/i);
  hit.priceDisplay = sold && display ? text(htmlText(display[1])) : null;

  hit.bedrooms = count(property.bedrooms);
  hit.bathrooms = count(property.bathrooms);
  // total_parking counts garages + carports + open spaces; "parking" alone
  // was 0 on a page that shows one car space.
  hit.parking = count(property.total_parking) ?? count(property.parking);
  hit.propertyType = propertyTypeLabel(text(property.category));

  hit.landSize = sqm(property.area, property.area_unit);
  hit.landSizeUnit = hit.landSize ? "sqm" : null;
  const building = sqm(property.buildingDetails_area, property.buildingDetails_area_unit);
  if (building) {
    hit.propertySize = building;
    hit.propertySizeUnit = "sqm";
  }

  // GeoJSON point: [longitude, latitude].
  const coordinates = isRecord(property.location) ? property.location.coordinates : null;
  if (
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates.every((c) => typeof c === "number" && Number.isFinite(c) && c !== 0)
  ) {
    hit.latitude = coordinates[1];
    hit.longitude = coordinates[0];
  }

  const agents = Array.isArray(property.agents)
    ? property.agents.map((agent) => (isRecord(agent) ? text(agent.title) : null)).filter(Boolean)
    : [];
  hit.agentName = agents.length > 0 ? agents.join(", ") : null;
  const office = isRecord(property.office) ? text(property.office.title) : null;
  hit.agencyName = office ? `Barry Plant ${office}` : "Barry Plant";

  return hit;
}
